import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { postCandidateQuestionAsDiscussion } from "../../../redux/jobsSlice.ts";
import FileUploadSection from "./FileUploadSection";
import { QuestionStageHeader } from "./QuestionStageHeader";
import { QuestionFormField } from "./QuestionFormField";
import { QuestionImageUpload } from "./QuestionImageUpload";
import { QuestionActions } from "./QuestionActions";
import { McqOptions } from "./McqOptions";
import DraftEditor from "../../../components/DraftEditor/index.js";
import { PassageQuestionItem } from "./PassageQuestionItem.js";
import { toastNotification } from "../../../services/notification-service.js";
import toast from "react-hot-toast";
import { jobQuestionsparseExcelToJson } from "../../../util/jobQuestionsparseExcelToJson.js";
import { Switch } from "@headlessui/react";

export const JobQuestions = ({ data, setData }) => {
  const dispatch = useDispatch();
  const [openPublishModalIndex, setOpenPublishModalIndex] = useState(null);
  const [imagePreviews, setImagePreviews] = useState({});

  console.log("data", data);

  const AddQuestion = (stage) => {
    setData((prev) => {
      const currentQuestions = prev.additional_questions || [];
      const passageQuestions = prev.passage_questions || [];

      const maxQuestionId = Math.max(
        ...currentQuestions.map((q) => q.id),
        ...passageQuestions.map((q) => q.id),
        0
      );

      const stageQuestionCount = currentQuestions.filter(
        (q) => q.stage === stage && q.passage_id === null
      ).length;

      if (stageQuestionCount >= 20) {
        return prev;
      }

      const newQuestion = {
        id: maxQuestionId + 1,
        question: "",
        answer_type: "",
        options: null,
        correct_answer: "",
        time_limit_seconds: 30,
        stage: stage,
        is_active: true,
        has_image: false,
        image: null,
        imageName: "",
        passage_id: null,
        is_justified: false,
      };

      return {
        ...prev,
        additional_questions: [...currentQuestions, newQuestion],
      };
    });
  };

  const AddQuestionWithPassage = (stage) => {
    setData((prev) => {
      const currentPassageQuestions = prev.passage_questions || [];
      const currentAdditionalQuestions = prev.additional_questions || [];

      const maxPassageId = Math.max(
        ...currentPassageQuestions.map((q) => q.id),
        0
      );
      const maxQuestionId = Math.max(
        ...currentAdditionalQuestions.map((q) => q.id),
        ...currentPassageQuestions.map((q) => q.id),
        0
      );

      const newPassageId = maxPassageId + 1;
      const firstQuestionId = maxQuestionId + 1;
      const secondQuestionId = maxQuestionId + 2;

      const newPassage = {
        id: newPassageId,
        title: `Passage ${currentPassageQuestions.length + 1}`,
        content: "",
      };

      const firstQuestion = {
        id: firstQuestionId,
        question: "",
        answer_type: "",
        options: null,
        correct_answer: "",
        time_limit_seconds: 30,
        stage: stage,
        is_active: true,
        has_image: false,
        image: null,
        imageName: "",
        passage_id: newPassageId,
        is_justified: false,
      };

      return {
        ...prev,
        passage_questions: [...currentPassageQuestions, newPassage],
        additional_questions: [...currentAdditionalQuestions, firstQuestion],
      };
    });
  };

  const AddQuestionToPassage = (passageId, stage) => {
    setData((prev) => {
      const currentAdditionalQuestions = prev.additional_questions || [];

      const maxQuestionId = Math.max(
        ...currentAdditionalQuestions.map((q) => q.id),
        0
      );

      const newQuestion = {
        id: maxQuestionId + 1,
        question: "",
        answer_type: "",
        options: null,
        correct_answer: "",
        time_limit_seconds: 30,
        stage: stage,
        is_active: true,
        has_image: false,
        image: null,
        imageName: "",
        passage_id: passageId,
        is_justified: false,
      };

      return {
        ...prev,
        additional_questions: [...currentAdditionalQuestions, newQuestion],
      };
    });
  };

  const DeleteQuestion = (index) => {
    setData((prev) => ({
      ...prev,
      additional_questions: prev.additional_questions.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const DeletePassage = (passageId) => {
    toastNotification({
      status: "warning",
      message: (
        <div>
          <p>
            Are you sure you want to delete this passage and all its questions?
          </p>
          <div className="flex justify-center gap-4 mt-2">
            <button
              onClick={() => {
                toast.dismiss();
                confirmDelete(passageId);
              }}
              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
            >
              Delete
            </button>
            <button
              onClick={() => toast.dismiss()}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      duration: 5000,
    });
  };

  const confirmDelete = (passageId) => {
    setData((prev) => {
      const updatedPassageQuestions =
        prev.passage_questions?.filter((p) => p.id !== passageId) || [];

      const updatedAdditionalQuestions =
        prev.additional_questions?.filter((q) => q.passage_id !== passageId) ||
        [];

      toastNotification({
        status: "success",
        message: "Passage and questions deleted successfully",
      });

      return {
        ...prev,
        passage_questions: updatedPassageQuestions,
        additional_questions: updatedAdditionalQuestions,
      };
    });
  };

  const handleQuestionChange = (index, e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      additional_questions: prev.additional_questions.map((q, i) =>
        i === index
          ? {
              ...q,
              [name]: value,
              ...(name === "answer_type" && value !== "mcq"
                ? { options: null }
                : {}),
              ...(name === "answer_type" && value === "boolean"
                ? { correct_answer: "true" }
                : {}),
              ...(name === "answer_type" && value !== "boolean"
                ? { correct_answer: "" }
                : {}),
            }
          : q
      ),
    }));
  };

  const handlePublishDiscussion = (data) => {
    dispatch(postCandidateQuestionAsDiscussion(data));
  };

  const renderQuestions = (stage) => {
    const stageQuestions = (data?.additional_questions || []).filter(
      (q) => q.stage === stage && q.passage_id === null
    );

    return (
      <div className="w-full mb-8">
        <QuestionStageHeader stage={stage} onAddQuestion={AddQuestion} />

        {stageQuestions.length === 0 && (
          <div className="text-sm text-gray-500 mb-4">
            No questions added yet for{" "}
            {stage === 1 ? "Preliminary Assessment" : "Second Round"}
          </div>
        )}

        {stageQuestions.map((question, index) => {
          const fullIndex = data.additional_questions.findIndex(
            (q) => q?.id === question?.id
          );

          const showOptionsField = question.answer_type === "mcq";
          const showCorrectAnswerField = question.answer_type !== "url";
          const isURLType =
            question.answer_type === "url" ||
            question.answer_type === "varchar";

          return (
            <div
              key={fullIndex}
              className="md:grid grid-cols-12 w-full md:space-y-0 space-y-6 gap-6 mb-6 p-4 bg-gray-50 rounded-lg"
            >
              {[
                {
                  id: `question-${fullIndex}`,
                  name: "question",
                  type: "text",
                  value: question.question || "",
                  onChange: (e) => handleQuestionChange(fullIndex, e),
                  className:
                    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
                  placeholder: " ",
                  maxLength: 255,
                  required: true,
                  label: `Question ${fullIndex + 1}*`,
                  colSpan: "col-span-12 md:col-span-5",
                },
                {
                  id: `type-${fullIndex}`,
                  name: "answer_type",
                  type: "select",
                  value: question.answer_type || "",
                  onChange: (e) => handleQuestionChange(fullIndex, e),
                  className:
                    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
                  required: true,
                  label: "Type*",
                  options: [
                    { value: "", label: "Select Type" },
                    ...(question.stage !== 1
                      ? [{ value: "url", label: "URL" }]
                      : [
                          { value: "boolean", label: "True/False" },
                          { value: "varchar", label: "Short Answer" },
                          { value: "mcq", label: "Multiple Choice" },
                        ]),
                  ],
                  colSpan: "col-span-12 md:col-span-2",
                },
                {
                  id: `time-${fullIndex}`,
                  name: "time_limit_seconds",
                  type: "number",
                  value: question.time_limit_seconds || "",
                  onChange: (e) => {
                    const rawValue = e.target.value;
                    handleQuestionChange(fullIndex, {
                      target: {
                        name: "time_limit_seconds",
                        value: rawValue,
                      },
                    });
                  },
                  onBlur: (e) => {
                    const value = Math.min(
                      600,
                      Math.max(5, parseInt(e.target.value) || 5)
                    );
                    handleQuestionChange(fullIndex, {
                      target: {
                        name: "time_limit_seconds",
                        value: isNaN(value) ? 5 : value,
                      },
                    });
                  },
                  className:
                    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
                  placeholder: " ",
                  min: 5,
                  max: 600,
                  required: true,
                  label: "Time Limit (5-600 sec)*",
                  colSpan: "col-span-12 md:col-span-2",
                },
              ].map((field, fieldIndex) => (
                <div key={fieldIndex} className={`${field.colSpan}`}>
                  <QuestionFormField {...field} />
                </div>
              ))}

              {/* Combined Image Upload and Justification Toggle */}
              {stage === 1 && (
                <div className="col-span-12 md:col-span-2 flex items-center justify-between">
                  <QuestionImageUpload
                    setData={setData}
                    questionIndex={fullIndex}
                    hasImage={question.has_image}
                    setImagePreviews={setImagePreviews}
                    imagePreviews={imagePreviews}
                    question={question}
                  />

                  {(question.answer_type === "boolean" ||
                    question.answer_type === "mcq") && (
                    <div className="flex items-center gap-2 ml-4">
                      <label
                        htmlFor={`is_justified-${fullIndex}`}
                        className="text-sm font-medium text-gray-700 whitespace-nowrap"
                      >
                        Justify
                      </label>
                      <Switch
                        id={`is_justified-${fullIndex}`}
                        name="is_justified"
                        checked={question.is_justified || false}
                        onChange={(enabled) =>
                          handleQuestionChange(fullIndex, {
                            target: {
                              name: "is_justified",
                              value: enabled,
                            },
                          })
                        }
                        className={`${
                          question.is_justified
                            ? "bg-orange-600"
                            : "bg-gray-200"
                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500`}
                      >
                        <span
                          className={`${
                            question.is_justified
                              ? "translate-x-6"
                              : "translate-x-1"
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="col-span-12 md:col-span-1 flex items-center justify-center space-x-2">
                <QuestionActions
                  questionIndex={fullIndex}
                  openPublishModalIndex={openPublishModalIndex}
                  setOpenPublishModalIndex={setOpenPublishModalIndex}
                  handlePublishDiscussion={handlePublishDiscussion}
                  onDeleteQuestion={DeleteQuestion}
                  question={question}
                />
              </div>

              {/* MCQ Options */}
              {showOptionsField && (
                <McqOptions
                  questionIndex={fullIndex}
                  question={question}
                  onOptionsChange={(updatedFields) => {
                    setData((prev) => ({
                      ...prev,
                      additional_questions: prev.additional_questions.map(
                        (q, i) => {
                          if (i !== fullIndex) return q;
                          return {
                            ...q,
                            ...updatedFields,
                          };
                        }
                      ),
                    }));
                  }}
                />
              )}

              {/* Correct Answer */}
              {showCorrectAnswerField && (
                <div className="col-span-12 mt-4">
                  <QuestionFormField
                    id={`correct-${fullIndex}`}
                    name="correct_answer"
                    type="text"
                    value={question.correct_answer || ""}
                    onChange={(e) => handleQuestionChange(fullIndex, e)}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer"
                    placeholder=" "
                    required={!isURLType}
                    label={`Correct Answer${!isURLType ? "*" : ""}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderQuestionsWithPassage = (stage) => {
    const stagePassages = data?.passage_questions || [];

    return (
      <div className="w-full mb-8">
        <div className="flex gap-x-2 items-center mb-4">
          <h3 className="text-lg font-normal text-orange-600">
            {stage === 1
              ? "Preliminary English Aptitude Test"
              : "Preliminary Assessment"}{" "}
            (Passage)
          </h3>
          <button
            type="button"
            onClick={() => AddQuestionWithPassage(stage)}
            className="text-orange-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </button>
        </div>

        {stagePassages.length === 0 && (
          <div className="text-sm text-gray-500 mb-4">
            No passages added yet
          </div>
        )}

        {stagePassages.map((passage) => {
          const passageQuestions = (data?.additional_questions || []).filter(
            (q) => q.passage_id === passage.id
          );

          return (
            <div
              key={passage.id}
              className="mb-8 border rounded-lg p-4 bg-gray-50"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex-1 mr-4">
                  <input
                    type="text"
                    value={passage.title}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, 255); // Enforce max length
                      setData((prev) => ({
                        ...prev,
                        passage_questions: prev.passage_questions.map((p) =>
                          p.id === passage.id ? { ...p, title: newValue } : p
                        ),
                      }));
                    }}
                    maxLength={255}
                    className="w-full md:text-lg text-base font-medium border-b border-gray-300 focus:border-orange-500 outline-none bg-transparent"
                  />
                  <div className="text-xs text-gray-500 text-right mt-1">
                    {passage.title.length}/255 characters
                  </div>
                </div>
                <QuestionActions
                  isPassage={true}
                  onDeletePassage={() => DeletePassage(passage.id)}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passage Content*
                </label>
                <DraftEditor
                  title={passage?.content}
                  onChange={(content) => {
                    setData((prev) => ({
                      ...prev,
                      passage_questions: prev.passage_questions.map((p) =>
                        p.id === passage.id ? { ...p, content } : p
                      ),
                    }));
                  }}
                  limit={1500}
                  initialContent={passage.content || ""}
                />
              </div>

              <div className="flex justify-between items-center mb-4">
                <div className="flex mb-4 items-center gap-x-2">
                  <h4 className="font-normal text-orange-600 border-b pb-2">
                    Questions for this passage
                  </h4>
                  <svg
                    onClick={() => AddQuestionToPassage(passage.id, stage)}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6 text-orange-600 cursor-pointer"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </div>
                {/* <button
                  onClick={() => AddQuestionToPassage(passage.id, stage)}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  Add Question
                </button> */}
              </div>

              {passageQuestions.length === 0 && (
                <div className="text-sm text-gray-500 mb-4">
                  No questions added for this passage yet
                </div>
              )}

              {passageQuestions.map((question) => (
                <PassageQuestionItem
                  key={question.id}
                  question={question}
                  stage={stage}
                  data={data}
                  setData={setData}
                  imagePreviews={imagePreviews}
                  setImagePreviews={setImagePreviews}
                  openPublishModalIndex={openPublishModalIndex}
                  setOpenPublishModalIndex={setOpenPublishModalIndex}
                  handlePublishDiscussion={handlePublishDiscussion}
                  DeleteQuestion={DeleteQuestion}
                  handleQuestionChange={handleQuestionChange}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <div className="flex flex-col justify-start items-start col-span-12 -mt-3">
      <FileUploadSection
        data={data}
        setData={setData}
        setImagePreviews={setImagePreviews}
        parseFunction={jobQuestionsparseExcelToJson}
      />
      {renderQuestionsWithPassage(1)}
      {renderQuestions(1)}
      {renderQuestions(2)}
    </div>
  );
};
